import { useCallback, useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import { setCurrentMode } from '@/lib/api';

/**
 * Fleet Mode Phase 3 — driver-shell mode hook.
 *
 * Single source of truth for the PWA's three mode surfaces:
 *   - Top-nav FLEET / PUBLIC pill
 *   - Profile page "Fleet Mode" CategoryRow
 *   - Account sheet "Public Charging" toggle
 *
 * All three read `mode` from the same auth-store user object and call
 * the same `switchTo` mutation; the cache update inside `switchTo`
 * propagates synchronously to every consumer in the same render.
 *
 * Optimistic write
 * ────────────────
 * The auth store is mutated BEFORE the network round-trip so the
 * pill flips instantly on tap (the toggle is a 1:1 user signal — no
 * server-side validation other than "are you in a fleet" — so the
 * optimistic UX is honest). On rejection we roll back the cache and
 * surface the server error via the toast queue.
 *
 * canSwitch
 * ─────────
 * False when the user has no fleet attribution (`user.fleet_id` is
 * null). The mode mechanism is meaningless for non-fleet drivers —
 * their current_mode stays 'public' forever — so every caller should
 * gate their UI on canSwitch. The server enforces the same predicate
 * with a 403 NOT_IN_A_FLEET on attempted writes; this client check is
 * for UX, not security.
 */
export interface UseCurrentMode {
  mode: 'fleet' | 'public';
  canSwitch: boolean;
  isSwitching: boolean;
  switchTo: (next: 'fleet' | 'public') => Promise<void>;
  /** Convenience helper — flips to the opposite mode. */
  toggle: () => Promise<void>;
}

export function useCurrentMode(): UseCurrentMode {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [isSwitching, setIsSwitching] = useState(false);

  // current_mode is optional on PublicUser for backward compat with
  // legacy auth-store payloads predating Phase 3. Coerce undefined →
  // 'public' so the hook always returns a defined mode value.
  const mode: 'fleet' | 'public' = user?.current_mode ?? 'public';
  const canSwitch = Boolean(user?.fleet_id);

  const switchTo = useCallback(
    async (next: 'fleet' | 'public'): Promise<void> => {
      if (!user) return;
      if (next === mode) return; // Idempotent no-op (matches server).
      // Block redundant mutations while one is in flight — the
      // toggle UX fires on every tap and we don't want a quick
      // double-tap to race two requests with opposing intents.
      if (isSwitching) return;

      const previous = user;
      setIsSwitching(true);
      // Optimistic — flip the auth-store user immediately so the
      // pill / row label / toggle state all update on the same
      // render the tap landed on.
      setUser({ ...user, current_mode: next });
      try {
        await setCurrentMode(next);
        toast(
          next === 'fleet' ? 'Switched to Fleet Mode' : 'Switched to Public Mode',
          { tone: 'accent' },
        );
      } catch (err) {
        // Roll back and surface the server message. The endpoint can
        // return 403 NOT_IN_A_FLEET if the client gate disagreed
        // with the server (e.g. just-removed-from-fleet race).
        setUser(previous);
        const message = err instanceof Error ? err.message : 'Failed to switch mode';
        toast(message, { tone: 'error' });
      } finally {
        setIsSwitching(false);
      }
    },
    [user, mode, isSwitching, setUser],
  );

  const toggle = useCallback(
    () => switchTo(mode === 'fleet' ? 'public' : 'fleet'),
    [mode, switchTo],
  );

  return { mode, canSwitch, isSwitching, switchTo, toggle };
}
